import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import { InvestigationButton } from "./InvestigationButton";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import {
  deleteInvestigationAssign,
  patchInvestigation,
} from "../../../../../apis";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { OutlineData, OutlineHierarchy } from "../../../../Icon";
import { EGIconButton } from "../../../../MainComponents/Button/IconButton/IconButton";
import Icon from "../../../../Icon/Icon";

export const InvestigationHeader = ({
  id,
  reportDetail,
  investigationList,
  investigation,
  isLoading,
  selectedInvestigation,
  updateSelectedInvestigation,
  reportRender,
  setLoadingInvestigation,
  setShowModalAddInvestigation,
  openConfirmDeleteModal,
}) => {
  const addInvestigationValue = "additional_investigation";

  const [t_investigation] = useTranslation("investigation");
  const [t_report] = useTranslation("report");
  const theme = useTheme();

  const [invTitle, setInvTitle] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [tryToSaveTitle, setTryToSaveTitle] = useState(false);

  // Función para mostrar texto plano como label
  const getInvLabel = ({ advance, title }) => {
    if (title) return title;

    const tipo = t_investigation(
      advance ? "investigation.advanced" : "investigation.basic"
    );

    const clasificacion = reportDetail?.classification?.label;

    return `${tipo} ${
      clasificacion
        ? `- ${clasificacion}`
        : `- ${t_investigation("Sin clasificación")}`
    }`;
  };

  // Lista de investigaciones parseada
  const paresedInvestigations = useMemo(() => {
    return [
      ...investigationList.map((item) => ({
        ...item,
        title: getInvLabel(item),
      })),
      { title: `+ ${t_investigation("header.add_investigation")}`, alias: addInvestigationValue },
    ];
  }, [investigationList, reportDetail]);

  // Sync cuando cambia la investigación seleccionada
  useEffect(() => {
    setInvTitle(
      investigationList.find((item) => item.alias === selectedInvestigation)
        ?.title || ""
    );
    setIsEditing(false);
    setTryToSaveTitle(false);
  }, [selectedInvestigation]);

  // Guardar nombre editado
  const hdlSaveTitle = async () => {
    setTryToSaveTitle(true);
    if (!(invTitle.length > 2)) return;

    try {
      await patchInvestigation(selectedInvestigation, {
        folder: { title: invTitle },
      });
      reportRender();
      setIsEditing(false);
    } catch (err) {
      console.error(err);
    }
  };

  // Cambiar selección
  const handleChangeSelectedInvestigation = (e) => {
    if (e.target.value === addInvestigationValue) {
      setShowModalAddInvestigation(true);
    } else {
      updateSelectedInvestigation(e.target.value, true);
    }
  };

  // Desvincular
  const handleUnlinkInvestigation = async () => {
    setLoadingInvestigation(true);
    const load = enqueueSnackbar(`${t_report("General.loading")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      await deleteInvestigationAssign(selectedInvestigation, id);
      closeSnackbar(load);
      enqueueSnackbar(
        t_investigation("investigation.investigation_unlinked_success"),
        { variant: "success" }
      );
      setLoadingInvestigation(false);
      reportRender();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        t_investigation("error_unlinking_investigation_from_report")
      );
      setLoadingInvestigation(false);
    }
  };

  return (
    <Grid2 mt={2} container spacing={2}>
      {/* Panel izquierdo */}
      <Grid2
        size={{ xs: 12, lg: 8 }}
        className="dyTheme1 dyBorder1 rounded"
        display="grid"
        gridTemplateColumns="5px auto"
        overflow="hidden"
      >
        <Box
          sx={{
            backgroundColor: investigation.advance
              ? theme.palette.ethicsBlue.main
              : theme.palette.green.main,
          }}
        />

        <Stack
          padding={2}
          flexDirection="row"
          justifyContent="space-between"
          spacing={1}
        >
          {/* Título e ícono */}
          <Stack direction="row" alignItems="center" flexGrow={1} spacing={1}>
            <Box
              height={24}
              width={24}
              borderRadius={0.8}
              backgroundColor={
                investigation.advance
                  ? theme.palette.ethicsBlue.main
                  : theme.palette.green.main
              }
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {investigation.advance ? (
                <OutlineData color={theme.palette.white.main} size={16} />
              ) : (
                <OutlineHierarchy color={theme.palette.white.main} size={16} />
              )}
            </Box>

            {isEditing ? (
              <EGInput
                value={invTitle}
                label={t_investigation("header.investigation_name")}
                onChange={(e) => setInvTitle(e.target.value)}
                variant="outlined"
                isValid={tryToSaveTitle ? invTitle.length > 2 : null}
                showSuccessValidation
                showHelpText={false}
                sx={{ width: "100%" }}
              />
            ) : (
              <Box display="flex" alignItems="center">
                <Typography
                  component="span"
                  fontWeight={600}
                  fontSize={20}
                  marginLeft={1}
                >
                  {invTitle || getInvLabel(investigation)}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Botones */}
          <Stack
            flexDirection="row"
            marginTop={{ xs: 1.2, lg: 0 }}
            alignItems="center"
          >
            {!isEditing ? (
              <>
                <Box display={{ xs: "none", sm: "block" }}>
                  <InvestigationButton
                    sx={{ marginRight: 1 }}
                    icon="trash_can"
                    iconHover="bold_trash"
                    iconColor={theme.palette.red.main}
                    iconColorHover={theme.palette.red.main}
                    size="small"
                    onClick={handleUnlinkInvestigation}
                  />
                </Box>
                <Box display={{ xs: "none", sm: "block" }}>
                  <InvestigationButton
                    icon="outline_edit"
                    iconHover="bold_edit"
                    size="small"
                    onClick={() => {
                      const currentTitle =
                        investigationList.find(
                          (item) => item.alias === selectedInvestigation
                        )?.title || "";
                      setInvTitle(currentTitle);
                      setIsEditing(true);
                      setTryToSaveTitle(false);
                    }}
                  />
                </Box>
              </>
            ) : (
              <>
                <Box display={{ xs: "none", sm: "block" }} marginRight={1}>
                  <EGIconButton
                    iconName="close_icon"
                    iconColor="white"
                    color="error"
                    title={t_investigation("header.cancel")}
                    placement="bottom"
                    onClick={() => {
                      setIsEditing(false);
                      setInvTitle(
                        investigationList.find(
                          (item) => item.alias === selectedInvestigation
                        )?.title || ""
                      );
                    }}
                    sx={{ height: 24, width: 24, padding: 0 }}
                  />
                </Box>
                <Box display={{ xs: "none", sm: "block" }}>
                  <EGIconButton
                    iconName="check_icon"
                    iconColor="white"
                    color="success"
                    title={t_investigation("header.save")}
                    placement="bottom"
                    onClick={hdlSaveTitle}
                    sx={{ height: 24, width: 24, padding: 0 }}
                  />
                </Box>
              </>
            )}
          </Stack>
        </Stack>
      </Grid2>

      {/* Panel derecho - selector */}
      <Grid2
        className="dyTheme1 dyBorder1 rounded"
        display="flex"
        alignItems="center"
        size={{ xs: 12, lg: 4 }}
        padding={1.4}
      >
        <EGBasicSelect
          options={paresedInvestigations}
          optionLabel="title"
          optionValue="alias"
          label={t_investigation("header.select_investigation")}
          value={selectedInvestigation}
          onChange={handleChangeSelectedInvestigation}
          disabled={isLoading}
        />
      </Grid2>
    </Grid2>
  );
};
